import React, { useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { MapPin, Loader2, Home as HomeIcon, Briefcase, ShieldCheck, PlayCircle, Radius } from "lucide-react";
import { api } from "@/lib/api";
import { useLocation as useLoc } from "@/context/LocationContext";

/**
 * Property P1 · Map+list split browse page.
 *
 * - Left half: MapLibre GL map with a pin per listing, coloured by price
 *   band (green cheap → red expensive).
 * - Right half: scroll-list of the same listings with commute-time to a
 *   "work" address the user drops. Commute time is computed live via our
 *   own `/api/rides/multi-quote` route engine (Cab) — zero third-party
 *   licensing needed. That's the unique moat: no competitor overlays their
 *   own route engine on top of their property inventory.
 */
const _OPENFREE_LIBERTY = "https://tiles.openfreemap.org/styles/liberty";
const _MAPLIBRE_DEMO    = "https://demotiles.maplibre.org/style.json";

function _priceBand(inr) {
  if (!inr) return "#a1a1aa";       // grey unknown
  if (inr < 20000) return "#059669"; // green
  if (inr < 40000) return "#f59e0b"; // amber
  if (inr < 70000) return "#ef4444"; // red
  return "#7c3aed";                   // purple luxury
}

// Small YouTube/Vimeo/mp4 thumbnail extractor — used by the video-tour badge.
function _videoThumbnail(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube") || u.hostname.includes("youtu.be")) {
      const id = u.hostname === "youtu.be" ? u.pathname.slice(1) : u.searchParams.get("v");
      if (id) return `https://img.youtube.com/vi/${id}/mqdefault.jpg`;
    }
  } catch { /* ignore */ }
  return null;
}

// Haversine — km between two lat/lng points.
function _haversineKm(a, b) {
  if (a?.lat == null || a?.lng == null || b?.lat == null || b?.lng == null) return null;
  const toRad = (d) => d * Math.PI / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat/2)**2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng/2)**2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

async function _commuteMinutes(from, to) {
  try {
    const r = await api.post("/rides/multi-quote", {
      services:    ["ride"],   // Sedan is the neutral ETA benchmark
      pickup_lat:  from.lat, pickup_lng: from.lng,
      dropoff_lat: to.lat,   dropoff_lng: to.lng,
    });
    return r.data?.quotes?.[0]?.eta_minutes ?? null;
  } catch { return null; }
}

export default function PropertyBrowse() {
  const { loc } = useLoc();
  const centre = useMemo(() => ({
    lat: typeof loc?.lat === "number" ? loc.lat : 19.076,
    lng: typeof loc?.lng === "number" ? loc.lng : 72.877,
  }), [loc]);

  const [items,    setItems]    = useState([]);
  const [loading,  setL]        = useState(true);
  const [work,     setWork]     = useState(null);   // {lat,lng,label}
  const [workText, setWorkText] = useState("");
  const [commute,  setCommute]  = useState({});     // { listing_id: minutes }
  const [selected, setSelected] = useState(null);   // hovered/tapped listing_id
  // Property P2 · radius filter (km) around either the pickup pin or the user's location.
  const [radiusKm, setRadiusKm] = useState(0);       // 0 = off, otherwise 1–10 km
  const [pin,      setPin]      = useState(null);    // {lat,lng} — draggable pin for pickup-around

  const mapWrap = useRef(null);
  const mapRef  = useRef(null);
  const markers = useRef([]);

  // --- Load listings around the user's location ---
  useEffect(() => {
    setL(true);
    api.get("/listings", {
      params: { category: "property", lat: centre.lat, lng: centre.lng, radius_km: 25, limit: 60 },
    }).then(r => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setL(false));
  }, [centre.lat, centre.lng]);

  // Property P2 · filter items by radius around the pin (or centre when no pin).
  const anchor = pin || centre;
  const filtered = useMemo(() => {
    if (!radiusKm) return items;
    return items.filter(it => {
      const d = _haversineKm(anchor, { lat: it.lat, lng: it.lng });
      return d == null || d <= radiusKm;
    });
  }, [items, radiusKm, anchor.lat, anchor.lng]);

  // --- Initialise the map once ---
  useEffect(() => {
    if (!mapWrap.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: mapWrap.current,
      style: _OPENFREE_LIBERTY,
      center: [centre.lng, centre.lat],
      zoom: 12,
      attributionControl: false,
    });
    map.on("error", e => {
      if (String(e?.error?.message || "").includes("tiles")) {
        try { map.setStyle(_MAPLIBRE_DEMO); } catch { /* ignore */ }
      }
    });
    // Property P2 · click anywhere on the map to drop / move the radius pin.
    map.on("click", (ev) => {
      // Ignore clicks that landed on a marker (they set `selected` themselves).
      if (ev.originalEvent?.target?.closest?.("[data-testid^='property-map-pin-']")) return;
      setPin({ lat: ev.lngLat.lat, lng: ev.lngLat.lng });
    });
    mapRef.current = map;
    return () => { try { map.remove(); } catch { /* ignore */ } mapRef.current = null; };
    // eslint-disable-next-line
  }, []);

  // --- Re-plot markers whenever items or selection change ---
  useEffect(() => {
    if (!mapRef.current) return;
    markers.current.forEach(m => m.remove());
    markers.current = [];
    // Draw the radius pin as a distinct marker so users can see the anchor.
    if (pin) {
      const el = document.createElement("div");
      el.setAttribute("data-testid", "property-radius-pin");
      el.style.cssText = "width:24px;height:24px;border-radius:50%;background:#0F5132;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.35);";
      const rm = new maplibregl.Marker({ element: el, draggable: true }).setLngLat([pin.lng, pin.lat]).addTo(mapRef.current);
      rm.on("dragend", () => {
        const p = rm.getLngLat();
        setPin({ lat: p.lat, lng: p.lng });
      });
      markers.current.push(rm);
    }
    filtered.forEach(it => {
      if (typeof it.lat !== "number" || typeof it.lng !== "number") return;
      const el = document.createElement("button");
      el.setAttribute("data-testid", `property-map-pin-${it.listing_id}`);
      const isSel = selected === it.listing_id;
      el.style.cssText = `
        background:${_priceBand(it.price)};
        color:#fff;font-weight:800;font-size:${isSel ? 12 : 10}px;
        padding:${isSel ? "6px 10px" : "3px 7px"};
        border-radius:999px;border:2px solid ${isSel ? "#0F5132" : "#fff"};
        box-shadow:0 2px 6px rgba(0,0,0,0.25);cursor:pointer;`;
      el.textContent = it.price ? `₹${Math.round(it.price / 1000)}k` : "…";
      el.addEventListener("click", () => setSelected(it.listing_id));
      const m = new maplibregl.Marker({ element: el })
        .setLngLat([it.lng, it.lat]).addTo(mapRef.current);
      markers.current.push(m);
    });
    // Fit bounds if we have ≥ 2 pins.
    if (filtered.length >= 2 && mapRef.current) {
      const b = new maplibregl.LngLatBounds();
      filtered.forEach(it => it.lat && it.lng && b.extend([it.lng, it.lat]));
      if (!b.isEmpty()) mapRef.current.fitBounds(b, { padding: 32, duration: 400, maxZoom: 14 });
    }
  }, [filtered, selected, pin]);

  // --- Compute commute-time for every listing when work is set ---
  useEffect(() => {
    if (!work || items.length === 0) { setCommute({}); return; }
    let alive = true;
    (async () => {
      const map = {};
      // Sequential batching keeps the multi-quote endpoint honest on rate.
      for (const it of items) {
        if (!alive) return;
        if (typeof it.lat !== "number" || typeof it.lng !== "number") continue;
        const min = await _commuteMinutes({ lat: it.lat, lng: it.lng }, work);
        if (min != null) { map[it.listing_id] = min; setCommute({ ...map }); }
      }
    })();
    return () => { alive = false; };
  }, [work, items]);

  const setWorkFromText = async () => {
    if (!workText.trim()) return;
    try {
      const r = await api.get("/discover/search", { params: { q: workText, limit: 1 } });
      const first = (r.data?.results || r.data?.items || [])[0];
      if (first?.lat && first?.lng) {
        setWork({ lat: first.lat, lng: first.lng, label: first.name || workText });
      }
    } catch { /* ignore */ }
  };

  // Draw a radius circle on the map when the filter is on.
  useEffect(() => {
    if (!mapRef.current || !mapRef.current.isStyleLoaded()) return;
    const map = mapRef.current;
    const SRC = "prop-radius", LYR = "prop-radius-fill", LYRB = "prop-radius-line";
    try { if (map.getLayer(LYR))  map.removeLayer(LYR); }  catch {}
    try { if (map.getLayer(LYRB)) map.removeLayer(LYRB); } catch {}
    try { if (map.getSource(SRC)) map.removeSource(SRC); } catch {}
    if (!radiusKm) return;
    // GeoJSON circle polygon (64-side polygon approximation).
    const cx = anchor.lng, cy = anchor.lat;
    const kmPerDegLat = 111.32;
    const kmPerDegLng = Math.cos(cy * Math.PI / 180) * 111.32;
    const coords = [];
    for (let i = 0; i <= 64; i++) {
      const a = (i / 64) * Math.PI * 2;
      coords.push([cx + (radiusKm / kmPerDegLng) * Math.cos(a),
                   cy + (radiusKm / kmPerDegLat) * Math.sin(a)]);
    }
    map.addSource(SRC, { type: "geojson", data: { type: "Feature", geometry: { type: "Polygon", coordinates: [coords] } } });
    map.addLayer({ id: LYR,  type: "fill", source: SRC, paint: { "fill-color": "#10b981", "fill-opacity": 0.10 } });
    map.addLayer({ id: LYRB, type: "line", source: SRC, paint: { "line-color": "#059669", "line-width": 2, "line-dasharray": [3, 2] } });
  }, [radiusKm, anchor.lat, anchor.lng]);

  return (
    <div className="max-w-6xl mx-auto px-3 md:px-6 py-4" data-testid="property-browse-page">
      <div className="flex items-center gap-2 mb-2">
        <HomeIcon size={18} className="text-emerald-700" />
        <h1 className="heading text-2xl font-black text-stone-900">Property near you</h1>
      </div>

      {/* Work-address input — powers the commute overlay */}
      <div className="mb-3 bg-white border border-stone-200 rounded-full flex items-center gap-2 px-3 py-1.5" data-testid="property-work-input">
        <Briefcase size={14} className="text-stone-500" />
        <input
          value={workText}
          onChange={e => setWorkText(e.target.value)}
          onKeyDown={e => e.key === "Enter" && setWorkFromText()}
          placeholder='Your workplace ("Powai", "BKC", "Bandra")…'
          className="flex-1 outline-none text-sm bg-transparent"
        />
        <button
          onClick={setWorkFromText}
          className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 hover:text-emerald-800"
          data-testid="property-work-set"
        >
          Set work
        </button>
        {work && (
          <button
            onClick={() => { setWork(null); setCommute({}); setWorkText(""); }}
            className="text-[10px] text-stone-400 hover:text-stone-600"
            data-testid="property-work-clear"
          >
            ✕
          </button>
        )}
      </div>

      {/* Property P2 · radius filter — drops a pin on the map, filters listings within N km. */}
      <div className="mb-3 bg-white border border-stone-200 rounded-2xl px-3 py-2 flex items-center gap-3" data-testid="property-radius-panel">
        <Radius size={14} className="text-emerald-700 shrink-0" />
        <label className="text-[11px] font-bold uppercase tracking-widest text-stone-500 shrink-0">
          {radiusKm ? `Within ${radiusKm} km` : "All distances"}
        </label>
        <input
          type="range" min="0" max="10" step="1"
          value={radiusKm}
          onChange={e => setRadiusKm(parseInt(e.target.value, 10))}
          className="flex-1 accent-emerald-700"
          data-testid="property-radius-slider"
        />
        {radiusKm > 0 && !pin && (
          <span className="text-[10px] text-amber-700">Tap map to set anchor</span>
        )}
        {pin && (
          <button
            onClick={() => setPin(null)}
            className="text-[10px] text-stone-400 hover:text-stone-600"
            data-testid="property-radius-clear"
          >Clear pin</button>
        )}
        <button
          onClick={async () => {
            try {
              await api.post("/property/saved-searches", {
                anchor_lat: pin?.lat ?? centre.lat,
                anchor_lng: pin?.lng ?? centre.lng,
                radius_km:  radiusKm || null,
                city:       null,
                verified_only: false, video_only: false,
                frequency: "daily",
              });
              alert("Saved. We'll email you daily when new matching properties appear.");
            } catch (e) {
              alert(e?.response?.data?.detail || "Could not save — sign in first.");
            }
          }}
          className="text-[10px] font-bold uppercase tracking-widest text-white bg-emerald-700 hover:bg-emerald-800 px-2.5 py-1 rounded-full shrink-0"
          data-testid="property-save-search"
        >
          Save · Daily digest
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* MAP */}
        <div className="rounded-2xl overflow-hidden border border-stone-200 relative" style={{ height: "70vh" }}>
          <div ref={mapWrap} className="w-full h-full bg-stone-100" />
          <div className="absolute bottom-2 left-2 bg-white/95 rounded-full px-2 py-1 text-[10px] font-bold text-stone-700 shadow" data-testid="property-map-legend">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-600 mr-1 align-middle"/>&lt;20k
            <span className="inline-block w-2 h-2 rounded-full bg-amber-500 mx-1 align-middle"/>&lt;40k
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 mx-1 align-middle"/>&lt;70k
            <span className="inline-block w-2 h-2 rounded-full bg-purple-600 mx-1 align-middle"/>lux
          </div>
        </div>

        {/* LIST */}
        <div className="space-y-2 overflow-y-auto pr-1" style={{ maxHeight: "70vh" }} data-testid="property-list">
          {loading && <div className="text-center py-8 text-stone-400"><Loader2 className="inline animate-spin mr-2"/>Loading nearby properties…</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-8 text-stone-400 text-sm">
              {items.length === 0
                ? "No property listings yet within 25 km. Post one from /post-listing?category=property."
                : `No properties within ${radiusKm} km of the pin. Widen the radius or move the pin.`}
            </div>
          )}
          {filtered.map(it => {
            const thumb = _videoThumbnail(it.video_url);
            const distanceKm = pin ? _haversineKm(pin, { lat: it.lat, lng: it.lng }) : null;
            return (
              <button
                key={it.listing_id}
                onClick={() => {
                  setSelected(it.listing_id);
                  if (mapRef.current && it.lat && it.lng)
                    mapRef.current.easeTo({ center: [it.lng, it.lat], zoom: 15, duration: 350 });
                }}
                className={`w-full text-left bg-white border rounded-2xl p-3 transition-colors ${
                  selected === it.listing_id ? "border-emerald-500 shadow-sm" : "border-stone-200 hover:border-stone-300"
                }`}
                data-testid={`property-card-${it.listing_id}`}
              >
                <div className="flex gap-3">
                  {thumb && (
                    <div className="relative shrink-0 w-24 h-16 rounded-lg overflow-hidden bg-stone-200" data-testid={`property-video-${it.listing_id}`}>
                      <img src={thumb} alt="Video tour thumbnail" className="w-full h-full object-cover" />
                      <PlayCircle size={22} className="absolute inset-0 m-auto text-white drop-shadow" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="font-bold text-sm text-stone-900 truncate flex-1 flex items-center gap-1">
                        <span className="truncate">{it.title || "Property"}</span>
                        {it.verified && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5 shrink-0" data-testid={`property-verified-${it.listing_id}`}>
                            <ShieldCheck size={9}/>Verified
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-black text-emerald-700 tabular-nums">₹{(it.price || 0).toLocaleString("en-IN")}</div>
                    </div>
                    <div className="text-[11px] text-stone-500 truncate mt-0.5">
                      <MapPin size={9} className="inline -mt-0.5 mr-0.5"/>
                      {it.address || it.city || "Nearby"}
                      {distanceKm != null && <span className="ml-1 text-emerald-700">· {distanceKm.toFixed(1)} km</span>}
                    </div>
                    {work && (
                      <div className="text-[11px] text-stone-700 mt-1">
                        <Briefcase size={9} className="inline -mt-0.5 mr-1 text-stone-500"/>
                        {commute[it.listing_id] != null
                          ? <><span className="font-bold tabular-nums">{commute[it.listing_id]}</span> min to {work.label} by cab</>
                          : <span className="text-stone-400">Estimating commute…</span>}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
